import React from "react";
import { config } from "../../config/config";
import { activate, getUserPayments, getUserPaymentMethodWarningLabel } from "../../services/billingService";
import { isUserAccountActive } from "../../services/accountStatusService";
import { i18n } from "../../services/i18nService";
import { getVoucherDurationText } from "../../services/copyService";
import { Input, KeyboardSwap, withFocusable, Loading } from "../../services/visualLibraryService";
import {
  HeadingStyled,
  ContainerStyled,
  ButtonsGroupStyled,
  KeyButtonStyledNext,
  TextStyled
} from "../../styles/shared-styles";
import { VoucherRedemptionPageStyled, TextHeadingStyled, TextLabelStyled, BlockSignupStyled } from "./styles/Styled";
import { withAppState, getAppState } from "../../state";
import { withSubtheme } from "../../styles/themes/withSubtheme";
import { getGlobalButtonFocusPath } from "../../errors/globalButtons";
import { getInternetConnectivityModalConfig } from "../../errors/utils";
import { trackPage, trackEvent, getRedemptionSuccessTrackingData } from "../../services/analyticsService";
import { showExitSignupModal, showSingleModalButton } from "../../services/modalsService";
import { historyMethods } from "../../utils/historyMethods";
import { redirectTo } from "../../services/navigationService";
import { routes } from "../../routes";

const PAGE = "redeemVoucher";
const SIGNUP = "signup";
const NEXT_BUTTON_FOCUS_PATH = "redeemNextButton";
const KeyButtonStyledNextFocus = withFocusable(KeyButtonStyledNext);

function getInvalidDiscountModal() {
  return {
    modalText: i18n.get("pages.voucherRedemption.errors.generic"),
    buttons: showSingleModalButton("back", this.closeModal)
  };
}

function getVoucherPaymentWarningModal(paymentMethod) {
  const { modalText, buttons } = getUserPaymentMethodWarningLabel.call(this, paymentMethod);
  return {
    modalText,
    buttons
  };
}

function handleRequestError(error) {
  const { stateActions } = this.props;
  trackEvent(SIGNUP, "evoucher_redemption_error", error.message);
  if (error.status === 432 && error.error_code === "100004") {
    stateActions.showGlobalModal(getInvalidDiscountModal.call(this));
  } else if (error.status === 400) {
    stateActions.showGlobalModal(getInvalidDiscountModal.call(this));
  } else if (getAppState().isOnline && error.message.indexOf("timeout") > -1) {
    stateActions.showGlobalModal(
      getInternetConnectivityModalConfig({
        retryImplementation: this.closeModal,
        backImplementation: this.closeModal,
        customButtons: showSingleModalButton("back", this.closeModal),
        needsDismissableProp: false,
        isTimeout: true
      })
    );
  } else if (!getAppState().isOnline) {
    stateActions.showGlobalModal(
      getInternetConnectivityModalConfig({
        retryImplementation: this.closeModal,
        backImplementation: this.closeModal,
        customButtons: showSingleModalButton("back", this.closeModal),
        needsDismissableProp: false,
        isTimeout: true
      })
    );
  } else {
    stateActions.showGlobalModal({
      modalText: i18n.get("pages.voucherRedemption.errors.generic"),
      buttons: showSingleModalButton("back", this.closeModal)
    });
  }
}

class VoucherRedemption extends React.Component {
  constructor(props) {
    super(props);
    this.voucherInput = React.createRef();
    this.state = {
      voucherData: getAppState().voucherData,
      voucherNumber: "",
      isRedeeming: false,
      isLoaded: false
    };
    this.setDefaultFocus = this.setDefaultFocus.bind(this);
    this.getInputValue = this.getInputValue.bind(this);
    this.getDurationLabel = this.getDurationLabel.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.redeemVoucher = this.redeemVoucher.bind(this);
    this.keyboardDidSwap = this.keyboardDidSwap.bind(this);
    this.checkNextButtonDisability = this.checkNextButtonDisability.bind(this);
    this.checkKeyboardDisability = this.checkKeyboardDisability.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onBack = this.onBack.bind(this);
    this.showPaymentWarningModal = this.showPaymentWarningModal.bind(this);
    this.getUserPaymentMethods = this.getUserPaymentMethods.bind(this);
  }

  componentDidMount() {
    trackPage(PAGE);
    this.props.onRef && this.props.onRef(this);
    this.getUserPaymentMethods();
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.currentFocus !== this.props.currentFocus ||
      nextState.voucherNumber !== this.state.voucherNumber ||
      nextState.isRedeeming !== this.state.isRedeeming ||
      nextState.isLoaded !== this.state.isLoaded
    );
  }

  componentDidUpdate() {
    if (getAppState().globalModal) {
      return false;
    } else {
      !!this.state.voucherData && this.props.setFocus(NEXT_BUTTON_FOCUS_PATH);
    }
  }

  async getUserPaymentMethods() {
    this.userPayments = await getUserPayments();
    this.setState({ isLoaded: true });
  }

  showPaymentWarningModal() {
    const {
      stateActions,
      setFocus,
      applicationState: { userAccountStatus }
    } = this.props;
    const userPaymentMethod =
      this.userPayments &&
      this.userPayments.paymentMethods &&
      this.userPayments.paymentMethods.length &&
      this.userPayments.paymentMethods[0].paymentMethod;

    if (isUserAccountActive(userAccountStatus)) {
      // TODO: Implement /paymentPlans API to avoid mocking config data.
      const unmanagedPayment = config.get().unmanagedPaymentPlans.includes(userPaymentMethod);
      stateActions.showGlobalModal(getVoucherPaymentWarningModal.call(this, userPaymentMethod));
      setFocus(getGlobalButtonFocusPath(`${!!unmanagedPayment ? "accept" : "continue"}`));
    } else {
      this.redeemVoucher();
    }
  }

  async redeemVoucher() {
    const { voucherData, voucherNumber } = this.state;
    const {
      history: { location },
      setFocus
    } = this.props;
    try {
      this.setState({ isRedeeming: true });
      const paymentData = {
        paymentType: "voucher",
        paymentData: {
          voucherNumber: (voucherData && voucherData.voucher_code) || voucherNumber
        }
      };
      const updatedUser = await activate(paymentData);
      trackEvent(SIGNUP, "evoucher_redemption_success", getRedemptionSuccessTrackingData.call(this));
      this.setState({ isRedeeming: false });
      getAppState().userAccountStatus = updatedUser.settings.accountStatus;
      getAppState().voucherData = null;
      redirectTo.call(
        this,
        routes.thankyou,
        location && location.state && location.state.fromSignup && { fromSignup: location.state.fromSignup },
        historyMethods.REPLACE
      );
    } catch (e) {
      this.setState({ isRedeeming: false });
      handleRequestError.call(this, e);
      setFocus(getGlobalButtonFocusPath("back"));
    }
  }

  setDefaultFocus() {
    const { setFocus } = this.props;
    const currentDirection = i18n.getDirection().toUpperCase();
    const keyboardFocus = KeyboardSwap[`defaultFocusPath${currentDirection}`];
    setFocus(`${!!this.state.voucherData ? NEXT_BUTTON_FOCUS_PATH : keyboardFocus}`);
  }

  closeModal() {
    const {
      stateActions: { hideGlobalModal }
    } = this.props;
    hideGlobalModal();
    this.setDefaultFocus();
  }

  checkNextButtonDisability() {
    return (!!this.state.voucherData && this.state.voucherData.voucher_code) ||
      (!!this.state.voucherNumber && this.state.voucherNumber.length)
      ? false
      : true;
  }

  checkKeyboardDisability() {
    return !!this.state.voucherData ? true : false;
  }

  updateValue(value) {
    this.state.voucherNumber !== value && this.setState({ voucherNumber: value });
  }

  getInputValue() {
    const { voucherData, voucherNumber } = this.state;
    if (!!voucherData) {
      return voucherData.voucher_code;
    }
    return voucherNumber;
  }

  getDurationLabel() {
    const { voucherData } = this.state;
    return !!voucherData.duration && getVoucherDurationText(voucherData.duration.duration, true);
  }

  onBack() {
    const globalModal = getAppState().globalModal;
    if (globalModal && globalModal.dismissable && !getAppState().isOnline) {
      this.props.stateActions.hideGlobalModal();
    } else if (globalModal && globalModal.dismissHandler) {
      globalModal.dismissHandler();
    } else if (!globalModal || globalModal.dismissable) {
      showExitSignupModal.call(this);
    }
    return true;
  }

  keyboardDidSwap({ defaultFocusPath }) {
    this.props.setFocus(defaultFocusPath);
  }

  render() {
    return (
      <>
        <VoucherRedemptionPageStyled>
          {this.state.isLoaded ? (
            <>
              <HeadingStyled rank={1} text={i18n.get("pages.voucherRedemption.title")} />
              <TextHeadingStyled
                text={
                  !!this.state.voucherData
                    ? i18n.get("pages.voucherRedemption.text.eVoucher")
                    : i18n.get("pages.voucherRedemption.text.voucher")
                }
              />
              <ContainerStyled>
                <BlockSignupStyled>
                  <KeyboardSwap
                    text={this.state.voucherNumber}
                    onChange={this.updateValue}
                    direction={i18n.getDirection()}
                    onSwap={this.keyboardDidSwap}
                    disabled={this.checkKeyboardDisability()}
                    showEmailKeys={false}
                  />
                  <ButtonsGroupStyled>
                    <KeyButtonStyledNextFocus
                      onEnterPress={this.showPaymentWarningModal}
                      onMouseUp={this.showPaymentWarningModal}
                      focusPath={NEXT_BUTTON_FOCUS_PATH}
                      disabled={this.checkNextButtonDisability()}
                    >
                      {i18n.get("pages.voucherRedemption.nextButton")}
                    </KeyButtonStyledNextFocus>
                  </ButtonsGroupStyled>
                </BlockSignupStyled>
                <BlockSignupStyled>
                  <Input
                    id={"voucher-input"}
                    type="text"
                    ref={this.voucherInput}
                    value={this.getInputValue()}
                    label={i18n.get("pages.voucherRedemption.inputLabel")}
                    readOnly
                    className={`${
                      (!!this.state.voucherData && this.state.voucherData.voucher_code) || this.state.voucherNumber
                        ? "has-content"
                        : ""
                    }${this.state.voucherNumber ? " active" : ""}`}
                  />
                  {!!this.state.voucherData && this.state.voucherData.voucher_code && (
                    <TextLabelStyled text={this.getDurationLabel()} />
                  )}
                </BlockSignupStyled>
              </ContainerStyled>
              <TextStyled type="secondary" size="small" text={i18n.get("global.eVoucher.disclaimer")} />

              {this.state.isRedeeming && <Loading overlay />}
            </>
          ) : (
            <Loading overlay />
          )}
        </VoucherRedemptionPageStyled>
      </>
    );
  }
}

const Component = withAppState(
  withSubtheme(props => <VoucherRedemption currentFocus={props.applicationState.currentFocus} {...props} />)
);

Component.defaultFocusPathLTR = KeyboardSwap.defaultFocusPathLTR;
Component.defaultFocusPathRTL = KeyboardSwap.defaultFocusPathRTL;
Component.defaultFocusPath = KeyboardSwap.defaultFocusPath;

export default Component;
